import { useTranslation } from 'react-i18next';
import type { IconColor, IconName } from '../../icons/types';

export type BreadcrumbItem = {
  disabled?: boolean;
  href?: string;
  icon?: IconName;
  iconColor?: IconColor;
  text: string;
};

export type ResolvedBreadcrumbItem = BreadcrumbItem & {
  isDisabled: boolean;
  isInternalLink: boolean;
  isLast: boolean;
  isLink: boolean;
  key: string;
};

type UseBreadcrumbsParams = {
  ariaLabel?: string;
  items: BreadcrumbItem[];
};

type UseBreadcrumbsReturn = {
  resolvedAriaLabel: string;
  resolvedItems: ResolvedBreadcrumbItem[];
};

function useBreadcrumbs({ ariaLabel, items }: UseBreadcrumbsParams): UseBreadcrumbsReturn {
  const { t } = useTranslation();

  const resolvedAriaLabel = ariaLabel ?? t('common:BreadcrumbNavLabel', { defaultValue: 'Breadcrumb' });

  const resolvedItems: ResolvedBreadcrumbItem[] = items.map((item, index) => {
    const isLast = index === items.length - 1;
    const isDisabled = item.disabled === true;
    const isLink = !isDisabled && !isLast && Boolean(item.href);
    const isInternalLink = isLink && Boolean(item.href?.startsWith('/'));

    return {
      ...item,
      isDisabled,
      isInternalLink,
      isLast,
      isLink,
      key: `${index}-${item.text}`,
    };
  });

  return { resolvedAriaLabel, resolvedItems };
}

export default useBreadcrumbs;
